import React from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { APP_MENU } from '../../../../../constants'

// Pure helper: return the key of the accordion group that owns `pathname`, or
// null when none matches (e.g. the standalone Dashboard route or an unknown
// path). Standalone groups are skipped so they never drive accordion state.
// Extracted so it can be unit-tested in isolation and reused instead of
// duplicated in the component.
export const findGroupKeyForPath = (menu, pathname) => {
  if (!pathname) return null

  const owner = menu.find(
    group =>
      !group.standalone &&
      group.items.some(item => Boolean(matchPath({ path: item.path }, pathname)))
  )

  return owner ? owner.key : null
}

export const useAppMenu = (menuSource = APP_MENU) => {
  const { pathname } = useLocation()

  // Which group the current route lives in (drives auto-expand on load).
  const activeGroupKey = React.useMemo(
    () => findGroupKeyForPath(menuSource, pathname),
    [menuSource, pathname]
  )

  // The single group currently expanded. `null` means none is open. Seeded
  // from the active route's group so it auto-expands on load (AC5).
  const [openGroupKey, setOpenGroupKey] = React.useState(activeGroupKey)

  // When the route moves to a different group, expand that group (AC5). This
  // also collapses a group the user had opened before navigating away.
  const previousActiveRef = React.useRef(activeGroupKey)
  React.useEffect(() => {
    if (previousActiveRef.current !== activeGroupKey) {
      previousActiveRef.current = activeGroupKey
      setOpenGroupKey(activeGroupKey)
    }
  }, [activeGroupKey])

  const toggleGroup = React.useCallback(key => {
    // Single-open accordion: opening a group closes any other; clicking the
    // currently open group closes it, leaving none open (AC2, AC3, AC4).
    setOpenGroupKey(current => (current === key ? null : key))
  }, [])

  const menu = React.useMemo(
    () =>
      menuSource.map(group => ({
        ...group,
        open: group.standalone ? false : group.key === openGroupKey,
        containsActive: group.key === activeGroupKey,
      })),
    [menuSource, openGroupKey, activeGroupKey]
  )

  return { menu, openGroupKey, toggleGroup, activeGroupKey }
}
